import {m as motion} from 'framer-motion'
import React from 'react'
import styled from 'styled-components'

import {Accordion, AccordionItem} from '@/components/accordion'
import Image from '@/components/image'
import type {Props as RichTextProps} from '@/components/rich-text'
import RichText from '@/components/rich-text'
import {Text} from '@/components/text'
import {Section, Layout} from '@/styles/grid'
import {tokens} from '@/theme'
import type {ImageSource} from '@/types/image'
import {durationStringToNumber} from '@/utils/duration-string-to-number'
import mq from '@/utils/media-query'

interface Item {
    title: string
    body: RichTextProps['content']
    icon?: ImageSource
    image?: ImageSource
}

// Same as Item type but with required `image` prop
type ItemWithImage = Omit<Item, 'image'> & Required<Pick<Item, 'image'>>

export interface Props {
    title: string
    items: Item[]
    id?: string
    collapsible?: boolean
    dataTestId?: string
}

const TitleWithAccordion = ({id, title, items, collapsible, dataTestId}: Props) => {
    const [activeIndex, setActiveIndex] = React.useState(0)

    const hasImages = checkAllItemsHaveImages(items)

    return (
        <Layout
            id={id}
            as={Section}
            $variant="two-asymmetrical"
            $alignItems={hasImages ? 'center' : 'start'}
            aria-label={hasImages ? title : undefined}
            data-testid={dataTestId}
        >
            {!hasImages && (
                <Text $variant="heading-large" as="h2">
                    {title}
                </Text>
            )}

            {hasImages && (
                <ImagesWrapper>
                    {items.filter(checkItemHasImage).map((item, i) => (
                        <motion.div
                            initial={false}
                            variants={variants}
                            animate={i === activeIndex ? 'active' : 'inactive'}
                            key={i}
                            transition={{
                                type: 'tween',
                                duration: durationStringToNumber(tokens.slow) / 1000,
                            }}
                        >
                            <StyledImage
                                image={item.image}
                                width={550}
                                aria-hidden={i !== activeIndex}
                            />
                        </motion.div>
                    ))}
                </ImagesWrapper>
            )}

            <Accordion
                collapsible={collapsible}
                onChange={setActiveIndex}
                dataTestId={`${dataTestId}-accordion-container`}
            >
                {items.map(({title, body, icon}, i) => (
                    <AccordionItem key={`${title}-${i}`} title={title}>
                        <AccordionContentWrapper>
                            {icon && <Icon image={icon} width={60} aria-hidden />}

                            <RichText content={body} $variant="paragraph-base" $color="muted" />
                        </AccordionContentWrapper>
                    </AccordionItem>
                ))}
            </Accordion>
        </Layout>
    )
}

// Validates that this item has an image
const checkItemHasImage = (item: Item): item is ItemWithImage => Boolean(item.image?.url)

// Validates that ALL items have images
const checkAllItemsHaveImages = (items: Item[]) => items.every(checkItemHasImage)

const variants = {
    active: {
        opacity: 1,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'block',
        position: 'relative',
    },
    inactive: {
        opacity: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        position: 'absolute',
    },
} as const

const AccordionContentWrapper = styled.div`
    display: grid;
    align-items: center;
    column-gap: ${({theme}) => theme.spacings.s16};
    grid-template-columns: auto 1fr;
`

const Icon = styled(Image)`
    width: 40px;
    height: 40px;
    object-fit: contain;

    ${mq.max('sm')} {
        display: none;
    }
`

const ImagesWrapper = styled.div`
    position: relative;
    perspective: 1000px;

    ${mq.max('sm')} {
        order: 1;
    }
`

const StyledImage = styled(Image)`
    width: 100%;
`

export default TitleWithAccordion
